import { Suspense, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import './App.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-circular-progressbar/dist/styles.css';
import 'react-medium-image-zoom/dist/styles.css';
import '@fontsource/archivo-black';
import '@fontsource/dm-sans';
import AboutTab from './components/AboutTab';
import NetworkErrorModal from './components/ConnectionIssueModals/NetworkErrorModal';
import CustomIdleTimer from './components/custom_components/CustomIdleTimer';
import SubUserTab from './components/custom_components/SubUserTab/SubuserTab';
import DiabetesData from './components/DiabetesData/DiabetesData';
import ExamHint from './components/ExamHint';
import ForgotPassword from './components/Login/ForgotPassword';
import LoginForm from './components/Login/LoginForm';
import ResetPasswordWithOTP from './components/Login/ResetPasswordWithOTP';
import TeleophthStandaloneNewExam from './components/NewOrderPage/NewOrderPage';
import NotFound from './components/NotFound';
import SessionPreview from './components/SessionPreview/SessionPreview';
import SettingsTab from './components/SettingsTab/SettingsTab';
import useCheckClientVersion from './hooks/useCheckClientVersion';
import useGetCurrentUser from './hooks/useGetCurrentUser';
import useResetBlocked from './hooks/useResetBlocked';
import useSentryExamScope from './hooks/useSentryExamScope';
import useWatchDeployment from './hooks/useWatchDeployment';
import { CameraType } from './models/Camera.model';
import { Product } from './models/Product.model';
import TeleophthResults from './products/teleophth/pages/TeleophthResults/TeleophthResults';
import TeleophthStandaloneResults from './products/teleophthStandalone/pages/TeleophthStandaloneResults/TeleophthStandaloneResults';
import { PrivateRoute } from './routes';
import posthog, {
  registerSoftwareVersion,
  setPosthogGroups,
} from './services/posthog';
import { clearExamScopedGroups } from './services/posthogEvents';
import Sentry from './services/sentry';
import useRemoteBoundStore from './store/useRemoteBoundStore';
import { isExamInProgress } from './utils/isExamInProgress';

if ('$' in window) {
  window.$(() => {
    // dom ready
    window.bootbox.setDefaults({
      closeButton: false,
      animate: false,
    });

    // Disable Bootstrap's modal focus-trap. bootbox renders a
    // Bootstrap 5 modal whose focus enforcement fights react-bootstrap's: each
    // focusin handler re-fires .focus(), an unbounded loop that pegs the
    // renderer. bootbox exposes no per-modal options, so the global default is
    // the only lever.
    if (window.bootstrap?.Modal?.Default) {
      window.bootstrap.Modal.Default.focus = false;
    }
    window.bootbox.addLocale('vi', {
      OK: 'Xác nhận',
      CANCEL: 'Hủy bỏ',
      CONFIRM: 'Xác nhận',
    });
  });
}

function App() {
  useCheckClientVersion();
  useSentryExamScope();

  const [ipList, setIpList] = useState<string[]>([]);
  const [dataResetToken, setDataResetToken] = useState(0);
  const { isResetBlocked, setIsResetBlocked } = useResetBlocked();

  const {
    authInfo: { authToken },
    practiceSettings: { product },
    isConnected,
  } = useRemoteBoundStore();
  const { startDeploymentWatcher, stopDeploymentWatcher } =
    useWatchDeployment();

  useEffect(() => {
    // app icon
    const iconLink = document.createElement('link');
    iconLink.rel = 'icon';
    document.head.append(iconLink);
    iconLink.href = '/icons/app.ico';

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.append(appleLink);
    appleLink.href = '/icons/app.png';

    // app name
    document.title = 'Optain Workstation';
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      startDeploymentWatcher();
    }
    return () => {
      stopDeploymentWatcher();
    };
  }, [startDeploymentWatcher, stopDeploymentWatcher]);

  const { userId, practiceId, partnerId } = useGetCurrentUser();
  const sessionIid = useRemoteBoundStore((s) => s.session?.iid);

  useEffect(() => {
    if (!isExamInProgress(sessionIid)) {
      clearExamScopedGroups();
    }
  }, [sessionIid]);

  useEffect(() => {
    if (!authToken || !userId) {
      Sentry.setUser(null);
      Sentry.setTags({ product: null, cameraType: null });
      posthog.reset();
      registerSoftwareVersion();
      return;
    }
    Sentry.setUser({ id: userId });
    Sentry.setTags({ product, cameraType: CameraType.SMALL });
    posthog.identify(userId, {
      product,
      cameraType: CameraType.SMALL,
    });
    setPosthogGroups({
      product,
      practice: practiceId,
      partner: partnerId,
    });
  }, [authToken, userId, product, practiceId, partnerId]);

  const handleNetworkModalClose = () => {
    if (!isResetBlocked) {
      setDataResetToken((token) => token + 1);
    }
  };

  return (
    <>
      <Suspense fallback={null}>
        <Switch>
          <Route path="/login" strict exact>
            <LoginForm />
          </Route>

          <Route path="/forgot-password" strict exact>
            <ForgotPassword />
          </Route>

          <Route path="/reset-password-with-otp/:email" strict exact>
            <ResetPasswordWithOTP />
          </Route>

          {/*
            The keyed pages below are recreated when the network error modal
            closes, so they refetch instead of presenting data that no longer
            matches their filters. A keyed page holding data the user has
            entered but not submitted must say so through onResetBlockedChange,
            or the reset will discard it.
          */}
          <PrivateRoute path={['/', '/session-form']} strict exact>
            <TeleophthStandaloneNewExam
              key={dataResetToken}
              product={product}
              onResetBlockedChange={setIsResetBlocked}
            />
          </PrivateRoute>

          <PrivateRoute path="/session-hint" strict exact>
            <ExamHint />
          </PrivateRoute>

          <PrivateRoute path="/session-preview" strict exact>
            <SessionPreview />
          </PrivateRoute>

          <PrivateRoute path="/diabetes-data" strict exact>
            <DiabetesData />
          </PrivateRoute>

          <PrivateRoute path="/results" strict exact>
            {product === Product.TELEOPHTH_STANDALONE ? (
              <TeleophthStandaloneResults key={dataResetToken} />
            ) : (
              <TeleophthResults key={dataResetToken} />
            )}
          </PrivateRoute>

          <PrivateRoute path="/settings" strict exact>
            <SettingsTab ipList={ipList} setIpList={setIpList} />
          </PrivateRoute>

          <PrivateRoute path="/about" strict exact>
            <AboutTab />
          </PrivateRoute>

          <PrivateRoute path="/subuser" strict exact>
            <SubUserTab />
          </PrivateRoute>

          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Suspense>

      <Route path="/settings">
        {({ match }) =>
          !match &&
          !isConnected && (
            <NetworkErrorModal onClose={handleNetworkModalClose} />
          )
        }
      </Route>

      {authToken && <CustomIdleTimer />}
    </>
  );
}

export default App;
